import React, { useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CreditsModal from './CreditsModal';
import type { ModalParams } from '../../utils/modalUrlParams';

interface WithCreditsModalProps {
  children: (openCreditsModal: () => void) => ReactNode;
  handleUrlParams?: boolean;
  modalParams?: ModalParams | null;
}

/**
 * Higher Order Component that provides credits modal functionality
 *
 * @example
 * <WithCreditsModal>
 *   {(openCreditsModal) => (
 *     <Button onClick={openCreditsModal}>
 *       Manage Credits
 *     </Button>
 *   )}
 * </WithCreditsModal>
 */
export const WithCreditsModal: React.FC<WithCreditsModalProps> = ({
  children,
  handleUrlParams = false,
  modalParams,
}) => {
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsModalVariant, setCreditsModalVariant] = useState<
    'standard' | 'paymentSuccessful' | 'paymentFailed'
  >('standard');

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle payment status from query parameters if enabled
  useEffect(() => {
    if (!handleUrlParams) return;

    const purchaseStatus = searchParams.get('purchase');

    if (purchaseStatus === 'success') {
      setCreditsModalVariant('paymentSuccessful');
      setShowCreditsModal(true);
    } else if (purchaseStatus === 'cancelled') {
      setCreditsModalVariant('paymentFailed');
      setShowCreditsModal(true);
    }
  }, [handleUrlParams, searchParams]);

  const openCreditsModal = () => {
    setCreditsModalVariant('standard');
    setShowCreditsModal(true);
  };

  const closeCreditsModal = () => {
    setShowCreditsModal(false);
    setCreditsModalVariant('standard');

    // Remove the purchase query parameter from URL after modal is closed
    if (handleUrlParams) {
      const purchaseStatus = searchParams.get('purchase');
      if (purchaseStatus === 'success' || purchaseStatus === 'cancelled') {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('purchase');
        navigate(
          { pathname: location.pathname, search: newSearchParams.toString() },
          { replace: true }
        );
      }
    }
  };

  return (
    <>
      {children(openCreditsModal)}
      {showCreditsModal && (
        <CreditsModal
          variant={creditsModalVariant}
          onClose={closeCreditsModal}
          modalParams={modalParams}
        />
      )}
    </>
  );
};
